/**
 * "Establish sagas" handle establishing a call (ie. start or respond to a call).
 *
 * The sagas about starting a call locally assume there is no session established
 *    (since that's what it is doing). The sagas about responding to a call
 *    assume that there is a session (both webRTC and server).
 */

// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import { getCallById, getOptions, getTurnInfo } from '../../call/interfaceNew/selectors'
import { CALL_STATES, CALL_DIRECTION } from '../../call/constants'
import { checkBandwidthControls } from '../utils/bandwidth'

// Callstack plugin.
import { setupCall, answerWebrtcSession } from '../webrtc/establish'
import { closeCall } from '../webrtc/midcall'
import { getSessionById } from '../../webrtc/interface/selectors'
import { getIncomingRemoteTracks } from '../sdp/remoteTracks'

// Other plugins.
import { logManager } from '../../logs'
import BasicError, { callCodes } from '../../errors'

// Libraries.
import { call, put, select } from 'redux-saga/effects'

/**
 * Starts a new outgoing call.
 *
 * This saga defines how a call is started. It performs the webRTC and
 *    signalling operations to create a "call" both locally and on the server.
 * Assumptions:
 *     1. None.
 * Responsibilities:
 *     1. Setup the call locally, using the webRTC helper saga.
 *     2. Create the call on the server.
 *     3. Update call state (via redux action).
 * @method makeCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.createSession "Make call" signalling function.
 * @param {Object}   action        An action of type `MAKE_CALL`.
 */
export function * makeCall (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Initiating new outgoing call.')

  // Create a new local media object for this call.
  const mediaConstraints = {
    audio: action.payload.mediaConstraints.audio,
    video: action.payload.mediaConstraints.video,
    screenShare: action.payload.mediaConstraints.screenShare
  }

  const turnInfo = yield select(getTurnInfo)
  const callOptions = yield select(getOptions)

  const bandwidth = checkBandwidthControls(action.payload.bandwidth)

  const { error, offerSdp, sessionId, mediaIds } = yield call(setupCall, deps, mediaConstraints, {
    defaultPeerConfig: callOptions.defaultPeerConfig,
    turnInfo,
    bandwidth,
    dscpControls: action.payload.dscpControls,
    callId: action.payload.id,
    // ICE related configs.
    trickleIceMode: callOptions.trickleIceMode,
    removeBundling: callOptions.removeBundling,
    serverTurnCredentials: callOptions.serverTurnCredentials
  })

  // An error occurred while trying to setup the WebRTC portion of the call.
  //    Report the error and mark the call as ended.
  if (error) {
    log.info(`Failed to initiate call. Changing to ${CALL_STATES.ENDED}.`)
    yield put(
      callActions.makeCallFinish(action.payload.id, {
        state: CALL_STATES.ENDED,
        error: error
      })
    )

    return
  }

  const callInfo = {
    id: action.payload.id,
    participantAddress: action.payload.participantAddress,
    isAnonymous: action.payload.isAnonymous,
    from: action.payload.from,
    account: action.payload.account,
    offer: offerSdp,
    displayName: action.payload.displayName ? action.payload.displayName : '',
    customParameters: action.payload.customParameters,
    customBodies: action.payload.customBodies
  }

  const response = yield call(requests.createSession, callInfo)

  if (!response.error) {
    log.info(`Finished initiating call. Changing to ${CALL_STATES.INITIATED} and waiting on remote answer.`)

    // Get the list of all local tracks on the Session. Add the local tracks
    //    to call state now so they are available while the Call rings.
    const sessionState = yield select(getSessionById, sessionId)

    yield put(
      callActions.pendingMakeCall(action.payload.id, {
        state: CALL_STATES.INITIATED,
        // The ID that the backend uses to track this webRTC session.
        wrtcsSessionId: response.wrtcsSessionId,
        // The ID that the webRTC stack uses to track this webRTC session.
        webrtcSessionId: sessionId,
        // The local Media object associated with this call.
        mediaIds: mediaIds,
        // The bandwidth of the call.
        bandwidth,
        // The custom display name to use. Not supported on all environments.
        displayName: action.payload.displayName,
        // The custom parameters (headers) and custom bodies of the call
        customParameters: action.payload.customParameters,
        customBodies: action.payload.customBodies,
        localTracks: sessionState.localTracks
      })
    )
  } else {
    log.info('Failed to initiate call. Ending call and cleaning up WebRTC portions.')
    // The call failed, so stop the Media object created for the call.
    // TODO: Update redux state that the Media object is stopped.
    //    Need an event from Media model to notify about the stop, and listener
    //    set on Media when it is created (in `createLocalMedia` saga).
    yield call(closeCall, deps.webRTC, sessionId)

    yield put(
      callActions.makeCallFinish(action.payload.id, {
        state: CALL_STATES.ENDED,
        error: response.error
      })
    )
  }
}

/**
 * Sends ringing feedback to the server for an incoming, Initiated call.
 *
 * This saga defines how/when a call can send ringing feedback to the remote user.
 *    It performs signaling to send this notification to the server, which forwards
 *    it to the remote side of the call.
 * Assumptions:
 *    1. The call is in Initiated state.
 *    2. The call's direction is incoming.
 *    3. The SDK is configured `ringingFeedbackMode: 'manual'`.
 * Responsibilities:
 *    1. Perform signaling.
 *    2. Update call state (via redux action).
 * @method sendRingingFeedback
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateCallRinging "Send ringing" signalling function.
 * @param {Object}   action        An action of type `SEND_RINGING_FEEDBACK`.
 */
export function * sendRingingFeedback (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Sending ringing feedback for incoming call.')

  const options = yield select(getOptions)
  const targetCall = yield select(getCallById, action.payload.id)
  // Requirements:
  //  - call must exist
  //  - configuration must be 'manual'
  //  - call must be in Initiated state.
  //  - call must be incoming.
  let errorInfo
  if (!targetCall) {
    errorInfo = {
      message: `Failed to send ringing feedback. Call ${action.payload.id} not found.`,
      code: callCodes.INVALID_PARAM
    }
  } else if (options.ringingFeedbackMode !== 'manual') {
    errorInfo = {
      message: "Failed to send ringing feedback. Configuration must be set to 'manual' mode.",
      code: callCodes.NOT_SUPPORTED
    }
  } else if (targetCall.state !== CALL_STATES.INITIATED) {
    errorInfo = {
      message: `Failed to send ringing feedback. Call must be in ${CALL_STATES.INITIATED} state.`,
      code: callCodes.INVALID_STATE
    }
  } else if (targetCall.direction !== CALL_DIRECTION.INCOMING) {
    errorInfo = {
      message: 'Failed to send ringing feedback. Call must be incoming.',
      code: callCodes.INVALID_STATE
    }
  }

  // If the operation isn't allowable, dispatch an error and return.
  if (errorInfo) {
    log.info(errorInfo.message)
    yield put(
      callActions.sendRingingFeedbackFinish(action.payload.id, {
        error: new BasicError(errorInfo)
      })
    )
    return
  }

  const callInfo = { wrtcsSessionId: targetCall.wrtcsSessionId, id: targetCall.id }
  const response = yield call(requests.updateCallRinging, callInfo)

  if (response.error) {
    log.info(`Failed to send ringing feedback: ${response.error.code}: ${response.error.message}`)
    yield put(
      callActions.sendRingingFeedbackFinish(targetCall.id, {
        error: response.error
      })
    )
  } else {
    log.info(`Ringing feedback sent. Changing call to ${CALL_STATES.RINGING} state.`)
    yield put(callActions.sendRingingFeedbackFinish(targetCall.id))
  }
}

/**
 * Answers an incoming call.
 *
 * This saga defines how a call is answered. It performs the webRTC and
 *    signaling operations to respond to a call on the server. The local "call"
 *    may or may not exist at this point in time, depending if the call is slow
 *    start.
 * If the call is using regular negotiation, the call will exist locally.
 *    Signaling with an answer SDP will establish the call, so it can be
 *    considered "Connected" afterwards.
 * If the call is using slow start negotiation, the call needs to be created
 *    locally. An offer SDP from the call will be used for signaling (as per
 *    slow start), but the call cannot be considered "Connected" until we
 *    receive a response to the offer SDP.
 * Assumptions:
 *    1. The call is in Initiated or Ringing state.
 *    2. The call's direction is incoming.
 * Responsibilities:
 *    1. Determine whether Regular or Slow Start negotiation is to be used.
 *    2. Regular: Create an answer for the call, using the webRTC helpers.
 *    3. Regular: Update the call on the server with the answer.
 *    4. Regular: Update call state (via redux actions).
 *    2. Slow Start: Setup the call locally, using the webRTC helper saga.
 *    3. Slow Start: Update the call on the server with an offer.
 *    4. Slow Start: Update state (via redux actions).
 * @method answerCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.answerSession "Answer call" signalling function.
 * @param {Object}   action        An "answer call" action.
 */
export function * answerCall (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Answering incoming call.')

  const incomingCall = yield select(getCallById, action.payload.id)

  let errorInfo
  // Make sure that we can perform this operation right now.
  if (!incomingCall) {
    // Call must exist.
    errorInfo = {
      message: `Failed to answer call. Call ${action.payload.id} not found.`,
      code: callCodes.INVALID_PARAM
    }
  } else if (![CALL_STATES.INITIATED, CALL_STATES.RINGING].includes(incomingCall.state)) {
    // Call must be in Initiated or Ringing state.
    errorInfo = {
      message: `Failed to answer call. Call must be in ${CALL_STATES.INITIATED} or ${CALL_STATES.RINGING} state.`,
      code: callCodes.INVALID_STATE
    }
  } else if (incomingCall.direction !== CALL_DIRECTION.INCOMING) {
    // Call must be incoming.
    errorInfo = {
      message: 'Failed to answer call. Call must be incoming.',
      code: callCodes.INVALID_STATE
    }
  }

  // If the operation isn't allowable, dispatch an error and return.
  if (errorInfo) {
    log.info(errorInfo.message)
    yield put(
      callActions.answerCallFinish(action.payload.id, {
        error: new BasicError(errorInfo)
      })
    )
    return
  }

  const bandwidth = checkBandwidthControls(action.payload.bandwidth)
  const mediaConstraints = {
    audio: action.payload.mediaConstraints.audio,
    video: action.payload.mediaConstraints.video,
    screenShare: action.payload.mediaConstraints.screenShare
  }

  const wrtcsSessionId = incomingCall.wrtcsSessionId
  let webrtcInfo, callInfo, nextState
  if (incomingCall.isSlowStart) {
    /*
     * If the call was a slow start call, then it doesn't have a webRTC session
     *    yet. We need to setup the session and provide the signaling server
     *    with an SDP offer.
     */
    log.debug('Answering call with slow start negotiation.')

    const turnInfo = yield select(getTurnInfo)
    const callOptions = yield select(getOptions)

    // Setup a webRTC session.
    webrtcInfo = yield call(setupCall, deps, mediaConstraints, {
      callId: action.payload.id,
      defaultPeerConfig: callOptions.defaultPeerConfig,
      turnInfo,
      bandwidth,
      dscpControls: action.payload.dscpControls,
      // ICE related configs.
      trickleIceMode: callOptions.trickleIceMode,
      removeBundling: callOptions.removeBundling,
      serverTurnCredentials: callOptions.serverTurnCredentials
    })

    if (webrtcInfo.error) {
      log.info('Failed to answer incoming call.')
      yield put(
        callActions.answerCallFinish(
          action.payload.id,
          {
            error: webrtcInfo.error
          },
          {
            isSlowStart: incomingCall.isSlowStart
          }
        )
      )
      return
    }

    callInfo = {
      answer: webrtcInfo.offerSdp,
      wrtcsSessionId,
      customParameters: action.payload.customParameters,
      customBodies: action.payload.customBodies
    }

    // Even if the answer request is successful, we still need to wait for a
    //    notification from the signaling server to know if the call is complete.
    nextState = CALL_STATES.RINGING
  } else {
    /*
     * For a regular call scenario, we perform normal webRTC negotiation.
     */

    // Update the existing webRTC session with an answer.
    const sessionOptions = {
      sessionId: incomingCall.webrtcSessionId,
      bandwidth,
      dscpControls: action.payload.dscpControls,
      callId: incomingCall.id
    }
    webrtcInfo = yield call(answerWebrtcSession, deps, mediaConstraints, sessionOptions)

    if (webrtcInfo.error) {
      log.info('Failed to incoming answer call.')
      yield put(
        callActions.answerCallFinish(
          action.payload.id,
          {
            error: webrtcInfo.error
          },
          {
            isSlowStart: incomingCall.isSlowStart
          }
        )
      )

      return
    }

    callInfo = {
      id: incomingCall.id,
      answer: webrtcInfo.answerSDP,
      wrtcsSessionId,
      customParameters: action.payload.customParameters,
      customBodies: action.payload.customBodies
    }

    // If the answer request is successful, then the call can be considered complete.
    nextState = CALL_STATES.CONNECTED
  }

  // Perform the signaling to answer the call.
  const response = yield call(requests.answerSession, callInfo)

  let localTracks = []
  let remoteTracks = []
  if (!response.error) {
    if (incomingCall.isSlowStart) {
      log.info(`Finished answering slow-start call. Changing to ${nextState} and waiting for remote slow-start answer.`)
    } else {
      log.info(`Finished answering call. Changing to ${nextState}.`)

      // Get the list of all local tracks on the Session.
      const sessionState = yield select(getSessionById, incomingCall.webrtcSessionId) || {}
      localTracks = sessionState.localTracks

      // Get the list of all remote tracks being offered in this call. This is a
      //    new call, so this should be the full list of remote tracks available.
      const session = yield call([deps.webRTC.sessionManager, 'get'], incomingCall.webrtcSessionId)
      remoteTracks = yield call(getIncomingRemoteTracks, session)
    }

    yield put(
      callActions.answerCallFinish(
        action.payload.id,
        {
          state: nextState,
          // TODO: Proper start time for slow-start calls.
          startTime: Date.now(),
          // The local Media object associated with this call.
          mediaIds: webrtcInfo.mediaIds,
          // For slow start calls, there isn't a webRTC session yet.
          webrtcSessionId: webrtcInfo.sessionId,
          // TODO: Properly track the media that the call has.
          mediaConstraints,
          // The bandwidth of the call
          bandwidth,
          // The custom parameters (headers) and custom bodies of the call
          customParameters: action.payload.customParameters,
          customBodies: action.payload.customBodies,
          localTracks,
          // Add the remote tracks to the call state now that negotiation is done.
          remoteTracks
        },
        {
          isSlowStart: incomingCall.isSlowStart
        }
      )
    )
  } else {
    log.info('Failed to answer call.')
    yield put(
      callActions.answerCallFinish(
        action.payload.id,
        { error: response.error },
        { isSlowStart: incomingCall.isSlowStart }
      )
    )
  }
}

/**
 * Rejects an incoming call.
 *
 * This saga defines how a call is rejected. It performs the webRTC and
 *    signalling operations to reject a "call" both locally and on the server.
 * Assumptions:
 *    1. None
 * Responsibilities:
 *    1. Cleanup the call locally, using the webRTC helper saga.
 *    2. Reject the call on the server.
 *    3. Update call state (via redux action).
 * @method rejectCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.rejectSession "Reject call" signalling function.
 * @param {Object}   action        An action of type `REJECT_CALL`.
 */
export function * rejectCall (deps, action) {
  const { webRTC, requests } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Rejecting incoming call.')

  const incomingCall = yield select(getCallById, action.payload.id)

  let errorInfo
  // Ensure the call is in a valid state for this operation.
  if (!incomingCall) {
    // Call must exist.
    errorInfo = {
      message: `Failed to reject call. Call ${action.payload.id} not found.`,
      code: callCodes.INVALID_PARAM
    }
  } else if (![CALL_STATES.INITIATED, CALL_STATES.RINGING].includes(incomingCall.state)) {
    // Call must be in Initiated or Ringing state.
    errorInfo = {
      message: `Failed to reject call. Call must be in ${CALL_STATES.INITIATED} or ${CALL_STATES.RINGING} state.`,
      code: callCodes.INVALID_STATE
    }
  } else if (incomingCall.direction !== CALL_DIRECTION.INCOMING) {
    // Call must be incoming.
    errorInfo = {
      message: 'Failed to reject call. Call must be incoming.',
      code: callCodes.INVALID_STATE
    }
  }

  // If the operation isn't allowable, dispatch an error and return.
  if (errorInfo) {
    log.info(errorInfo.message)
    yield put(
      callActions.rejectCallFinish(action.payload.id, {
        error: new BasicError(errorInfo)
      })
    )
    return
  }

  yield call(closeCall, webRTC, incomingCall.webrtcSessionId)

  // Collect the information needed to make the request.
  const callInfo = {
    wrtcsSessionId: incomingCall.wrtcsSessionId
  }
  const response = yield call(requests.rejectSession, callInfo)

  if (!response.error) {
    log.info(`Finished rejecting call. Changing to ${CALL_STATES.ENDED}.`)
    yield put(callActions.rejectCallFinish(action.payload.id))
  } else {
    log.info('Failed to reject call.')
    yield put(
      callActions.rejectCallFinish(action.payload.id, {
        error: response.error
      })
    )
  }
}

/**
 * Ignores an incoming call.
 *
 * This saga defines how a call is ignored. It performs the webRTC
 *    operations to ignore a "call".
 * Assumptions:
 *    1. None
 * Responsibilities:
 *    1. Cleanup the call locally, using the webRTC helper saga.
 *    2. Update call state (via redux action).
 * @method ignoreCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   action        An action of type `IGNORE_CALL`.
 */
export function * ignoreCall (deps, action) {
  const { webRTC } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Ignoring incoming call.')

  const targetCall = yield select(getCallById, action.payload.id)

  let errorInfo
  // Ensure the call is in a valid state for this operation.
  if (!targetCall) {
    // Call must exist.
    errorInfo = {
      message: `Failed to ignore call. Call ${action.payload.id} not found.`,
      code: callCodes.INVALID_PARAM
    }
  } else if (![CALL_STATES.INITIATED, CALL_STATES.RINGING].includes(targetCall.state)) {
    // Call must be in Initiated or Ringing state.
    errorInfo = {
      message: `Failed to ignore call. Call must be in ${CALL_STATES.INITIATED} or ${CALL_STATES.RINGING} state.`,
      code: callCodes.INVALID_STATE
    }
  } else if (targetCall.direction !== CALL_DIRECTION.INCOMING) {
    // Call must be incoming.
    errorInfo = {
      message: 'Failed to ignore call. Call must be incoming.',
      code: callCodes.INVALID_STATE
    }
  }

  // If the operation isn't allowable, dispatch an error and return.
  if (errorInfo) {
    log.info(errorInfo.message)
    yield put(
      callActions.ignoreCallFinish(action.payload.id, {
        error: new BasicError(errorInfo)
      })
    )
    return
  }

  // Clean-up webRTC objects. Ignore any errors returned, since we want the
  //    call / session to be ended either way.
  yield call(closeCall, webRTC, targetCall.webrtcSessionId)

  log.info(`Finished ignoring call. Changing to ${CALL_STATES.ENDED}.`)
  // Report the operation complete.
  yield put(callActions.ignoreCallFinish(targetCall.id))
}

/**
 *
 * Forwards an incoming call.
 *
 * This saga handles the WebRTC and signalling portions of forwarding an incoming call
 * Assumptions:
 *    1. The action contains a destination address
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to forward the call to a specified destination address.
 *    2. Update call state (via redux actions).
 * @method forwardCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.forwardSession "Forward call" signalling function.
 * @param {Object}   action An action of type `FORWARD_CALL`.
 */
export function * forwardCall (deps, action) {
  const { webRTC, requests } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Forwarding incoming call.')

  const incomingCall = yield select(getCallById, action.payload.id)

  let errorInfo
  // Ensure the call is in a valid state for this operation.
  if (!incomingCall) {
    // Call must exist.
    errorInfo = {
      message: `Failed to forward call. Call ${action.payload.id} not found.`,
      code: callCodes.INVALID_PARAM
    }
  } else if (![CALL_STATES.INITIATED, CALL_STATES.RINGING].includes(incomingCall.state)) {
    // Call must be in Initiated or Ringing state.
    errorInfo = {
      message: `Failed to forward call. Call must be in ${CALL_STATES.INITIATED} or ${CALL_STATES.RINGING} state.`,
      code: callCodes.INVALID_STATE
    }
  } else if (incomingCall.direction !== CALL_DIRECTION.INCOMING) {
    // Call must be incoming.
    errorInfo = {
      message: 'Failed to forward call. Call must be incoming.',
      code: callCodes.INVALID_STATE
    }
  }

  // If the operation isn't allowable, dispatch an error and return.
  if (errorInfo) {
    log.info(errorInfo.message)
    yield put(
      callActions.forwardCallFinish(action.payload.id, {
        error: new BasicError(errorInfo)
      })
    )
    return
  }

  // Collect the information needed to make the request.
  const callInfo = {
    wrtcsSessionId: incomingCall.wrtcsSessionId,
    address: action.payload.destination
  }
  const response = yield call(requests.forwardSession, callInfo)

  if (response.error) {
    log.info('Failed to forward call.')
    yield put(
      callActions.forwardCallFinish(action.payload.id, {
        error: response.error
      })
    )
    return
  }

  yield call(closeCall, webRTC, incomingCall.webrtcSessionId)

  log.info(`Finished forwarding call. Changing to ${CALL_STATES.ENDED}.`)
  yield put(callActions.forwardCallFinish(action.payload.id))
}
